// src/pages/CicloDeVidaPage.jsx
// Ciclo de Vida de un Proyecto (solo admin): busca por ID de obra o por
// expediente y visualiza como GRAFO (echarts type:'graph') el estado del
// proyecto al centro y sus fases alrededor (expedientes, tramitación,
// circuito, seguimiento, pagos, mediciones, visitas).
import React, { useContext, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { AuthContext } from '../context/authContext';
import { useSession } from '../hooks/useSession';

const COLOR_ESTADO = {
	'Finalizado': '#15803d',
	'En Ejecución': '#2563eb',
	'Seguimiento': '#d97706',
	'En circuito administrativo': '#9333ea',
	'Análisis preliminar': '#6b7280',
	'Adenda': '#ea580c',
	'En guarda': '#0d9488',
	'Desestimada': '#dc2626',
};

const COLOR_FASE = {
	activa: '#16a34a',
	en_curso: '#f59e0b',
	pendiente: '#94a3b8',
};

const FASE_LABEL = {
	expedientes: 'Expedientes',
	tramitacion: 'Tramitación',
	circuito: 'Circuito admin.',
	seguimiento: 'Seguimiento',
	pagos: 'Pagos',
	mediciones: 'Mediciones',
	visitas: 'Visitas',
};

const CATEGORIAS = [
	{ name: 'Proyecto' },
	{ name: 'Fase', itemStyle: { color: '#2563eb' } },
	{ name: 'Expediente', itemStyle: { color: '#3b82f6' } },
	{ name: 'Tramitación', itemStyle: { color: '#8b5cf6' } },
	{ name: 'Circuito', itemStyle: { color: '#a855f7' } },
	{ name: 'Seguimiento', itemStyle: { color: '#0d9488' } },
	{ name: 'Pago', itemStyle: { color: '#0ea5e9' } },
	{ name: 'Medición', itemStyle: { color: '#14b8a6' } },
	{ name: 'Visita', itemStyle: { color: '#ec4899' } },
];

const CATEGORIA_IDX = { obra: 0, fase: 1, expediente: 2, tramitacion: 3, circuito: 4, seguimiento: 5, pago: 6, medicion: 7, visita: 8 };

// Posiciones radiales determinísticas: proyecto al centro, fases en un anillo
// y los ítems de cada fase abanico en un anillo exterior.
const calcularPosiciones = (grafo) => {
	const pos = new Map();
	pos.set('obra', { x: 0.5, y: 0.5 });
	const fases = grafo.nodos.filter(n => n.group === 'fase');
	const n = fases.length || 1;
	fases.forEach((fase, i) => {
		const theta = -Math.PI / 2 + (2 * Math.PI * i) / n;
		pos.set(fase.id, { x: 0.5 + 0.16 * Math.cos(theta), y: 0.5 + 0.16 * Math.sin(theta) });
		const hijos = grafo.nodos.filter(nd =>
			nd.id !== fase.id && grafo.aristas.some(a => a.source === fase.id && a.target === nd.id)
		);
		const c = hijos.length;
		if (c === 0) return;
		const spread = Math.min(0.42, (Math.PI / n) * 0.92);
		const r2 = 0.3 + 0.018 * Math.max(0, c - 3);
		hijos.forEach((hijo, j) => {
			const frac = c === 1 ? 0 : (2 * j / (c - 1)) - 1;
			const ang = theta + spread * frac;
			pos.set(hijo.id, { x: 0.5 + r2 * Math.cos(ang), y: 0.5 + r2 * Math.sin(ang) });
		});
	});
	return pos;
};

const fmtMonto = (val) => {
	const n = Number(val);
	if (val === null || val === undefined || val === '' || Number.isNaN(n)) return '-';
	return '$' + n.toLocaleString('es-AR');
};

const fmtFecha = (val) => {
	if (!val) return '-';
	const s = String(val);
	const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
	return m ? m[1] : s;
};

export default function CicloDeVidaPage() {
	const { role, token } = useContext(AuthContext);
	const { authenticatedFetch } = useSession();

	const [q, setQ] = useState('');
	const [busqueda, setBusqueda] = useState('');
	const [cargando, setCargando] = useState(false);
	const [error, setError] = useState('');
	const [resultado, setResultado] = useState(null); // { obra, grafo } | { candidatos } | { sinResultados }
	const [nodoSeleccionado, setNodoSeleccionado] = useState(null);

	const buscar = async (termino) => {
		const term = (termino || '').trim();
		if (!term) return;
		setBusqueda(term);
		setError('');
		setResultado(null);
		setNodoSeleccionado(null);
		setCargando(true);
		try {
			const params = new URLSearchParams({ q: term });
			const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
			const res = await authenticatedFetch(`/api/obras/ciclo-vida?${params.toString()}`, { headers });
			if (!res.ok) throw new Error(`Error ${res.status}`);
			setResultado(await res.json());
		} catch (err) {
			console.error('Error al buscar ciclo de vida:', err);
			setError(err.message);
		} finally {
			setCargando(false);
		}
	};

	const obra = resultado?.obra || null;
	const grafo = resultado?.grafo || null;
	const candidatos = resultado?.candidatos || null;
	const sinResultados = resultado?.sinResultados || false;

	// Opciones del grafo (solo cuando hay grafo)
	const option = useMemo(() => {
		if (!grafo) return null;
		const posiciones = calcularPosiciones(grafo);
		const data = grafo.nodos.map(nodo => ({
			...nodo,
			x: posiciones.get(nodo.id)?.x ?? 0.5,
			y: posiciones.get(nodo.id)?.y ?? 0.5,
			category: CATEGORIA_IDX[nodo.group] ?? 1,
		}));
		const links = grafo.aristas.map(a => ({ source: a.source, target: a.target }));

		return {
			backgroundColor: 'transparent',
			tooltip: {
				trigger: 'item',
				backgroundColor: 'rgba(255,255,255,0.97)',
				borderColor: '#e5e7eb',
				borderWidth: 1,
				textStyle: { color: '#1f2937', fontSize: 12 },
				formatter: (params) => {
					const d = params.data && params.data.detalle;
					if (!d || !Array.isArray(d)) return params.name;
					return [
						'<b>' + (params.data.label || params.name) + '</b>',
						...d.map(([k, v]) => `${k}: <b>${v}</b>`),
					].join('<br/>');
				},
			},
			legend: {
				data: CATEGORIAS.map(c => c.name),
				type: 'scroll',
				bottom: 0,
				icon: 'circle',
				itemWidth: 10,
				itemHeight: 10,
				textStyle: { fontSize: 11, color: '#374151' },
			},
			animationDuration: 600,
			series: [{
				type: 'graph',
				layout: 'none',
				roam: true,
				scaleLimit: { min: 0.35, max: 3.5 },
				edgeSymbol: ['none', 'none'],
				data,
				links,
				categories: CATEGORIAS,
				label: {
					show: true,
					position: 'right',
					color: '#374151',
					fontSize: 10,
				},
				lineStyle: { color: '#cbd5e1', width: 1.2, curveness: 0 },
				itemStyle: { borderColor: '#ffffff', borderWidth: 1.5 },
				emphasis: {
					focus: 'adjacency',
					fontWeight: 'bold',
					lineStyle: { width: 3, color: '#64748b' },
				},
			}],
		};
	}, [grafo]);

	if (role !== 'admin') {
		return (
			<div className="p-6 text-center">
				<div className="p-6 max-w-md mx-auto mt-10 text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
					Acceso denegado. Solo administradores.
				</div>
			</div>
		);
	}

	const nodoActivo = nodoSeleccionado && nodoSeleccionado.id !== 'obra' ? nodoSeleccionado : null;
	const estadoColor = obra ? (COLOR_ESTADO[obra.estado] || '#6b7280') : '#6b7280';

	return (
		<div className="p-6">
			<div className="flex items-center gap-3 mb-2">
				<h1 className="text-2xl font-bold text-gray-800 dark:text-white">Ciclo de Vida del Proyecto</h1>
<span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
				solo admin
			</span>
			</div>
			<p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
				Buscá por ID de obra (ej: 070-008) o por expediente (ej: EX2023-46356905) y se visualiza en qué instancia está el proyecto.
			</p>

			{/* Buscador */}
			<form
				className="flex gap-2 mb-6"
				onSubmit={(e) => { e.preventDefault(); buscar(q); }}
			>
				<input
					type="text"
					value={q}
					onChange={(e) => setQ(e.target.value)}
					placeholder="ID de obra o expediente..."
					className="flex-1 max-w-md rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
				/>
				<button
					type="submit"
					disabled={cargando || !q.trim()}
					className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
				>
					{cargando ? 'Buscando...' : 'Ver ciclo de vida'}
				</button>
			</form>

			{error && (
				<div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-md text-sm text-red-700 dark:text-red-300">
					Error: {error}
				</div>
			)}

			{/* Sin búsqueda aún */}
			{!resultado && !cargando && !error && (
				<div className="p-10 text-center text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800">
					Ingresá un ID de obra o un número de expediente para visualizar el ciclo de vida como un grafo.
				</div>
			)}

			{/* Sin resultados */}
			{sinResultados && (
				<div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-md text-sm text-amber-700 dark:text-amber-300">
					No se encontró ninguna obra para “{busqueda}”.
				</div>
			)}

			{/* Candidatos: el término puede matchear varias obras (ej: expediente en varias) */}
			{candidatos && (
				<div className="mb-6">
					<p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
						Se encontraron {candidatos.length} proyectos para “{busqueda}”. Elegí uno:
					</p>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
						{candidatos.map(c => (
							<button
								key={c.id}
								onClick={() => buscar(c.cg_01_id_partido_obra)}
								className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-left hover:border-blue-400 hover:shadow-sm transition-colors"
							>
								<div className="flex items-center justify-between mb-1">
									<span className="font-bold text-gray-800 dark:text-white">{c.cg_01_id_partido_obra}</span>
									<span
										className="px-2 py-0.5 text-[10px] font-semibold rounded-full text-white"
										style={{ backgroundColor: COLOR_ESTADO[c.cg_16_estado] || '#6b7280' }}
									>
										{c.cg_16_estado || 'Sin estado'}
									</span>
								</div>
								<div className="text-sm text-gray-600 dark:text-gray-300">{c.cg_02_nombre_obra}</div>
								<div className="text-xs text-gray-400 mt-1">{c.partido_nombre} · {c.area_responsable}</div>
							</button>
						))}
					</div>
				</div>
			)}

			{/* Grafo cargado */}
			{obra && grafo && (
				<>
					{/* Cabecera del proyecto */}
					<div className="mb-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
						<div className="flex flex-wrap items-start justify-between gap-4">
							<div>
								<div className="flex items-center gap-2 flex-wrap">
									<span className="text-2xl font-bold text-gray-800 dark:text-white">{obra.id_partido_obra}</span>
									<span
										className="px-2 py-0.5 text-xs font-semibold rounded-full text-white"
										style={{ backgroundColor: estadoColor }}
									>
										{obra.estado || 'Sin estado'}
									</span>
								</div>
								<div className="text-gray-600 dark:text-gray-300 mt-0.5">{obra.nombre}</div>
								<div className="text-xs text-gray-400 mt-1">
									{obra.partido} · {obra.area} · {obra.tipo_subsidio}
								</div>
							</div>
							<div className="flex gap-6 text-sm">
								<div>
									<div className="text-xs text-gray-400 uppercase">Monto</div>
									<div className="font-semibold text-gray-800 dark:text-white">{fmtMonto(obra.monto)}</div>
								</div>
								<div>
									<div className="text-xs text-gray-400 uppercase">Beneficiarios</div>
									<div className="font-semibold text-gray-800 dark:text-white">{Number(obra.beneficiarios || 0).toLocaleString('es-AR')}</div>
								</div>
								<div>
									<div className="text-xs text-gray-400 uppercase">Fecha carga</div>
									<div className="font-semibold text-gray-800 dark:text-white">{fmtFecha(obra.fecha_carga)}</div>
								</div>
							</div>
						</div>
						{/* Chips por fase */}
						<div className="mt-3 flex flex-wrap gap-2">
							{grafo.resumen.map(f => (
								<span
									key={f.key}
									className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
									style={{
										backgroundColor: COLOR_FASE[f.estado] + '1a',
										color: COLOR_FASE[f.estado],
										border: `1px solid ${COLOR_FASE[f.estado]}55`,
									}}
								>
									<span className="h-2 w-2 rounded-full" style={{ backgroundColor: COLOR_FASE[f.estado] }} />
									{FASE_LABEL[f.key] || f.nombre}
									{f.cantidad > 0 && `<${f.cantidad}>`}
								</span>
							))}
						</div>
						<button
							onClick={() => { setResultado(null); setQ(''); setBusqueda(''); }}
							className="mt-3 text-xs text-gray-500 hover:text-gray-700 underline"
						>
							← Nueva búsqueda
						</button>
					</div>

					{/* Grafo + panel de detalle */}
					<div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
						<div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
							{option && (
								<ReactECharts
									option={option}
									style={{ height: '640px', width: '100%' }}
									opts={{ renderer: 'canvas' }}
									onEvents={{
										click: (params) => {
											if (params && params.dataType === 'node' && params.data) {
												setNodoSeleccionado(params.data);
											}
										},
									}}
								/>
							)}
						</div>

						{/* Detalle del nodo seleccionado */}
						<div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-4 h-[640px] overflow-y-auto">
							{nodoActivo ? (
								<>
									<div className="flex items-center justify-between mb-3">
										<h3 className="font-semibold text-gray-800 dark:text-white">{nodoActivo.label || nodoActivo.name}</h3>
										<button
											onClick={() => setNodoSeleccionado(null)}
											className="text-xs text-gray-500 hover:text-gray-700 underline"
										>
											Volver al proyecto
										</button>
									</div>
									<dl className="space-y-2 text-sm">
										{Array.isArray(nodoActivo.detalle) && nodoActivo.detalle.map(([k, v], i) => (
											<div key={i} className="border-b border-gray-100 dark:border-gray-700 pb-1.5">
												<dt className="text-xs text-gray-400">{k}</dt>
												<dd className="text-gray-700 dark:text-gray-200">{v || '-'}</dd>
											</div>
										))}
									</dl>
								</>
							) : (
								<>
									<h3 className="font-semibold text-gray-800 dark:text-white mb-3">Detalle del Proyecto</h3>
									<dl className="space-y-2 text-sm">
										{grafo.nodos.find(n => n.id === 'obra')?.detalle?.map(([k, v], i) => (
											<div key={i} className="border-b border-gray-100 dark:border-gray-700 pb-1.5">
												<dt className="text-xs text-gray-400">{k}</dt>
												<dd className="text-gray-700 dark:text-gray-200">{v || '-'}</dd>
											</div>
										))}
									</dl>
									<p className="mt-4 text-xs text-gray-400">
										Hacé clic sobre cualquier nodo del grafo para ver su detalle. El color del Proyecto refleja el estado actual.
									</p>
								</>
							)}
						</div>
					</div>
				</>
			)}
		</div>
	);
}